import { formatCurrency, formatNumber } from '../../i18n/i18n';
import type { SimulationState } from '../../core/types';
import { calculateFinalScore } from '../../core/systems/PrestigeSystem';
import { getAllAchievements } from '../../core/systems/AchievementSystem';

function rankColor(rank: string): string {
  if (rank === 'S') return 'var(--color-warning)';
  if (rank === 'A') return 'var(--color-success)';
  if (rank === 'B') return 'var(--color-info)';
  return 'var(--color-text-primary)';
}

function createScorecard(state: SimulationState, title: string, borderColor: string): { card: HTMLDivElement; score: number; rank: string } {
  const { score, breakdown, rank } = calculateFinalScore(state);
  const achievements = state.achievements ?? [];
  const allAch = getAllAchievements();

  const card = document.createElement('div');
  card.style.cssText = `background:var(--color-bg-secondary);border:2px solid ${borderColor};border-radius:8px;padding:24px;max-width:440px;width:90vw;text-align:center;max-height:90vh;overflow-y:auto;`;

  const titleEl = document.createElement('h1');
  titleEl.textContent = title;
  titleEl.style.cssText = `color:${borderColor};font-size:24px;margin-bottom:4px;`;
  card.appendChild(titleEl);

  const sub = document.createElement('div');
  sub.textContent = `${state.company.name} — Year ${state.company.currentYear}`;
  sub.style.cssText = 'color:var(--color-text-secondary);font-size:13px;margin-bottom:16px;';
  card.appendChild(sub);

  const rankEl = document.createElement('div');
  rankEl.style.cssText = `font-size:48px;font-weight:700;color:${rankColor(rank)};margin-bottom:4px;font-family:var(--font-pixel);`;
  rankEl.textContent = rank;
  card.appendChild(rankEl);

  const scoreEl = document.createElement('div');
  scoreEl.style.cssText = 'font-size:18px;font-weight:600;margin-bottom:16px;font-family:var(--font-pixel);';
  scoreEl.textContent = `${score.toLocaleString()} pts`;
  card.appendChild(scoreEl);

  // Key stats grid
  const grid = document.createElement('div');
  grid.style.cssText = 'display:grid;grid-template-columns:1fr 1fr;gap:6px;margin-bottom:16px;text-align:center;';

  const addGridStat = (label: string, value: string, color?: string) => {
    const el = document.createElement('div');
    el.style.cssText = 'background:var(--color-bg-primary);border-radius:4px;padding:6px;';
    el.innerHTML = `<div style="font-size:9px;color:var(--color-text-muted);">${label}</div><div style="font-size:14px;font-weight:700;font-family:var(--font-pixel);${color ? `color:${color};` : ''}">${value}</div>`;
    grid.appendChild(el);
  };

  addGridStat('Games', String(state.company.totalProductsReleased));
  addGridStat('Hall of Fame', String(state.company.hallOfFameCount), 'var(--color-warning)');
  addGridStat('Revenue', formatCurrency(state.company.totalRevenue), 'var(--color-funds)');
  addGridStat('Fans', formatNumber(state.company.fans));
  addGridStat('Level', String(state.company.level), 'var(--color-xp)');
  addGridStat('Achievements', `${achievements.length}/${allAch.length}`);

  if (state.products.length > 0) {
    const best = [...state.products].sort((a, b) => b.averageReview - a.averageReview)[0]!;
    const worst = [...state.products].sort((a, b) => a.averageReview - b.averageReview)[0]!;
    addGridStat('Best', `${best.averageReview.toFixed(1)} ${best.name.slice(0, 12)}`, 'var(--color-success)');
    addGridStat('Worst', `${worst.averageReview.toFixed(1)} ${worst.name.slice(0, 12)}`, 'var(--color-danger)');
  }
  card.appendChild(grid);

  // Score breakdown
  const breakdownEl = document.createElement('div');
  breakdownEl.style.cssText = 'text-align:left;margin-bottom:16px;';
  const labels: Record<string, string> = {
    products: 'Products', hallOfFame: 'Hall of Fame', fans: 'Fans',
    revenue: 'Revenue', companyLevel: 'Level', staffLevel: 'Staff XP', loyalty: 'Loyalty',
  };
  for (const [key, pts] of Object.entries(breakdown)) {
    if (pts <= 0) continue;
    const row = document.createElement('div');
    row.style.cssText = 'display:flex;justify-content:space-between;font-size:10px;padding:1px 0;';
    row.innerHTML = `<span style="color:var(--color-text-muted);">${labels[key] ?? key}</span><span style="font-family:var(--font-pixel);color:var(--color-xp);">+${pts}</span>`;
    breakdownEl.appendChild(row);
  }
  card.appendChild(breakdownEl);

  // Timeline (if any)
  const timeline = (state as { timeline?: { year: number; week: number; text: string }[] }).timeline;
  if (timeline && timeline.length > 0) {
    const tlSection = document.createElement('div');
    tlSection.style.cssText = 'text-align:left;margin-bottom:16px;';
    const tlTitle = document.createElement('div');
    tlTitle.textContent = 'STUDIO TIMELINE';
    tlTitle.style.cssText = 'font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-bottom:4px;';
    tlSection.appendChild(tlTitle);

    // Show up to 15 key moments
    const shown = timeline.length > 15
      ? [...timeline.slice(0, 5), ...timeline.slice(-10)]
      : timeline;
    for (const entry of shown) {
      const row = document.createElement('div');
      row.style.cssText = 'display:flex;gap:6px;font-size:9px;padding:1px 0;';
      row.innerHTML = `<span style="color:var(--color-text-muted);flex-shrink:0;width:36px;">Y${entry.year}W${entry.week}</span><span style="color:var(--color-text-secondary);">${entry.text}</span>`;
      tlSection.appendChild(row);
    }
    if (timeline.length > 15) {
      const ellipsis = document.createElement('div');
      ellipsis.textContent = `... and ${timeline.length - 15} more events`;
      ellipsis.style.cssText = 'font-size:9px;color:var(--color-text-muted);text-align:center;';
      tlSection.appendChild(ellipsis);
    }
    card.appendChild(tlSection);
  }

  return { card, score, rank };
}

/**
 * Game Over screen — bankruptcy.
 */
export function showGameOverScreen(
  parent: HTMLElement,
  state: SimulationState,
  onRestart: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'game-ui';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:300;display:flex;align-items:center;justify-content:center;';

  const { card } = createScorecard(state, 'GAME OVER', 'var(--color-danger)');

  const msg = document.createElement('div');
  msg.textContent = 'Your studio has gone bankrupt.';
  msg.style.cssText = 'color:var(--color-danger);font-size:12px;margin-bottom:16px;';
  card.appendChild(msg);

  const btn = document.createElement('button');
  btn.className = 'btn btn--primary';
  btn.textContent = 'Back to Title';
  btn.style.cssText = 'padding:10px 24px;font-size:14px;';
  btn.addEventListener('click', () => { overlay.remove(); onRestart(); });
  card.appendChild(btn);

  overlay.appendChild(card);
  parent.appendChild(overlay);
}

/**
 * Campaign Complete screen — year 20.
 */
export function showCampaignEndScreen(
  parent: HTMLElement,
  state: SimulationState,
  onNewGamePlus: () => void,
  onContinue: () => void,
): void {
  const overlay = document.createElement('div');
  overlay.className = 'game-ui';
  overlay.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.85);z-index:300;display:flex;align-items:center;justify-content:center;';

  const { card } = createScorecard(state, 'CAMPAIGN COMPLETE', 'var(--color-warning)');

  // NG+ bonuses preview
  const prestigeLevel = (state.company.prestigeLevel ?? 0) + 1;
  const ngInfo = document.createElement('div');
  ngInfo.style.cssText = 'background:var(--color-bg-primary);border-radius:4px;padding:8px;margin-bottom:16px;text-align:left;';
  ngInfo.innerHTML = `
    <div style="font-size:10px;color:var(--color-text-muted);letter-spacing:1px;margin-bottom:4px;">NEW GAME+ BONUSES (Prestige ${prestigeLevel})</div>
    <div style="font-size:11px;color:var(--color-text-secondary);display:flex;flex-direction:column;gap:2px;">
      <span>+$${(2000 * prestigeLevel).toLocaleString()} starting funds</span>
      <span>+${prestigeLevel * 5}% XP bonus</span>
      <span>+${Math.min(prestigeLevel * 5, 20)} starting reputation</span>
      <span>Keep ${Math.min(prestigeLevel * 3, 15)} unlocks</span>
      <span>All achievements carry over</span>
    </div>
  `;
  card.appendChild(ngInfo);

  const btnRow = document.createElement('div');
  btnRow.style.cssText = 'display:flex;gap:8px;justify-content:center;flex-wrap:wrap;';

  const ngBtn = document.createElement('button');
  ngBtn.className = 'btn btn--primary';
  ngBtn.textContent = `New Game+ (Prestige ${prestigeLevel})`;
  ngBtn.style.cssText = 'padding:10px 20px;font-size:13px;';
  ngBtn.addEventListener('click', () => { overlay.remove(); onNewGamePlus(); });

  const contBtn = document.createElement('button');
  contBtn.className = 'btn';
  contBtn.textContent = 'Continue Playing';
  contBtn.style.cssText = 'padding:10px 20px;font-size:13px;';
  contBtn.addEventListener('click', () => { overlay.remove(); onContinue(); });

  btnRow.appendChild(ngBtn);
  btnRow.appendChild(contBtn);
  card.appendChild(btnRow);

  overlay.appendChild(card);
  parent.appendChild(overlay);
}
